import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * Toegankelijkheid — centrale live region announcer.
 *
 * Waarom één centrale announcer i.p.v. ad-hoc `aria-live` spans?
 *
 *  - **Unieke, stabiele ID's.** Elke prioriteit heeft exact één DOM-node
 *    (`#a11y-live-polite` / `#a11y-live-assertive`). Duplicates zouden
 *    schermlezers dwingen te kiezen welke ze voorlezen (implementation-
 *    specific) en kunnen leiden tot dubbele of overgeslagen meldingen.
 *
 *  - **Serialisatie via een FIFO-queue.** Meldingen worden één voor één
 *    in de region gepubliceerd. Twee `announce()`-calls direct achter
 *    elkaar overschrijven elkaar niet — de tweede wacht totdat de eerste
 *    daadwerkelijk in het DOM is verschenen én is voorgelezen (heuristiek
 *    op tijd, want DOM heeft geen "voorgelezen"-event).
 *
 *  - **Force re-announce bij identieke tekst.** Een schermlezer negeert
 *    een aria-live update als de nieuwe tekst identiek is aan de vorige.
 *    We clearen de region (`""`) → wachten 1 frame → schrijven de nieuwe
 *    tekst. Als de tekst identiek is aan de vorige melding hangen we
 *    een non-breaking space (`\u00A0`) toe om de tekst technisch uniek
 *    te maken zonder hoorbaar verschil.
 *
 *  - **Volgorde-garantie.** Elke melding krijgt een monotoon serie-
 *    nummer (`seq`). React's `key` op de tekstnode gebruikt dit nummer,
 *    zodat React niet per ongeluk twee updates in dezelfde flush
 *    combineert.
 *
 * Gebruik:
 *
 *   const { announce } = useAnnouncer();
 *   announce("Winkelmand geopend. 2 artikelen.");
 *   announce("Item verwijderd", { priority: "assertive" });
 */

type Priority = "polite" | "assertive";

type QueueItem = {
  seq: number;
  text: string;
  priority: Priority;
};

type AnnouncerCtx = {
  announce: (message: string, opts?: { priority?: Priority }) => void;
};

const Ctx = createContext<AnnouncerCtx | null>(null);

// Publieke, stabiele DOM-ID's zodat tests, integraties en devtools ze
// betrouwbaar kunnen selecteren.
export const A11Y_LIVE_POLITE_ID = "a11y-live-polite";
export const A11Y_LIVE_ASSERTIVE_ID = "a11y-live-assertive";

// Tijd tussen clear → write (moet ≥ 1 frame zijn zodat SR de mutatie ziet).
const CLEAR_TO_WRITE_MS = 60;
// Minimale zichtbaarheid van een melding voor SR uitspreekt (heuristiek).
const HOLD_MS = 900;

export function AnnouncerProvider({ children }: { children: ReactNode }) {
  const [politeText, setPoliteText] = useState("");
  const [politeSeq, setPoliteSeq] = useState(0);
  const [assertiveText, setAssertiveText] = useState("");
  const [assertiveSeq, setAssertiveSeq] = useState(0);

  const queueRef = useRef<QueueItem[]>([]);
  const seqRef = useRef(0);
  const runningRef = useRef(false);
  // Laatst voorgelezen tekst per priority — voor identieke-tekst-dedupe.
  const lastTextRef = useRef<Record<Priority, string>>({ polite: "", assertive: "" });
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  const clearTimer = useCallback((t: ReturnType<typeof setTimeout>) => {
    clearTimeout(t);
    timersRef.current.delete(t);
  }, []);

  const setTimer = useCallback((fn: () => void, ms: number) => {
    const t = setTimeout(() => {
      timersRef.current.delete(t);
      fn();
    }, ms);
    timersRef.current.add(t);
    return t;
  }, []);

  const processQueue = useCallback(() => {
    if (runningRef.current) return;
    const next = queueRef.current.shift();
    if (!next) return;
    runningRef.current = true;

    const write = (text: string) => {
      if (next.priority === "polite") {
        setPoliteText(text);
        setPoliteSeq(next.seq);
      } else {
        setAssertiveText(text);
        setAssertiveSeq(next.seq);
      }
    };

    // Stap 1: clear (dwingt SR om de volgende mutatie als "nieuw" te zien).
    write("");
    // Stap 2: schrijf de nieuwe tekst na een frame.
    setTimer(() => {
      let text = next.text;
      // Identiek aan de vorige → maak technisch uniek met NBSP-suffix.
      if (text === lastTextRef.current[next.priority]) {
        text = text + "\u00A0";
      }
      lastTextRef.current[next.priority] = text;
      write(text);
      // Stap 3: houd de tekst kort staan, daarna volgende in queue.
      setTimer(() => {
        runningRef.current = false;
        processQueue();
      }, HOLD_MS);
    }, CLEAR_TO_WRITE_MS);
  }, [setTimer]);

  const announce = useCallback<AnnouncerCtx["announce"]>(
    (message, opts) => {
      const text = String(message ?? "").trim();
      if (!text) return;
      seqRef.current += 1;
      queueRef.current.push({
        seq: seqRef.current,
        text,
        priority: opts?.priority ?? "polite",
      });
      processQueue();
    },
    [processQueue],
  );

  useEffect(() => {
    return () => {
      // Ruim alle openstaande timers op bij unmount (bv. hot reload).
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current.clear();
      queueRef.current = [];
      runningRef.current = false;
    };
  }, []);

  const value = useMemo<AnnouncerCtx>(() => ({ announce }), [announce]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <span
        id={A11Y_LIVE_POLITE_ID}
        data-a11y-live="polite"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        // Key op basis van seq: React vervangt de tekstnode i.p.v. te patchen,
        // wat sommige SR-implementaties helpt om de nieuwe waarde op te pikken
        // ook wanneer de string maar minimaal is veranderd.
        key={`polite-${politeSeq}`}
      >
        {politeText}
      </span>
      <span
        id={A11Y_LIVE_ASSERTIVE_ID}
        data-a11y-live="assertive"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
        key={`assertive-${assertiveSeq}`}
      >
        {assertiveText}
      </span>
    </Ctx.Provider>
  );
}

export function useAnnouncer(): AnnouncerCtx {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Fail-soft: als de provider ontbreekt (bv. in tests), gebruik een no-op
    // zodat consumenten niet crashen. Wel loggen in dev.
    if (typeof console !== "undefined" && process.env.NODE_ENV !== "production") {
      console.warn("[a11y] useAnnouncer() zonder <AnnouncerProvider>; melding wordt niet uitgesproken.");
    }
    return { announce: () => {} };
  }
  return ctx;
}
